import {
  AI_INSTRUCTION_FILTERABLE_FIELDS,
  AI_INSTRUCTION_TYPES,
} from '@kirin/api-client/contracts/ai-instructions';
import type { AiInstructionFilterableField } from '@kirin/api-client/contracts/ai-instructions';

export const AI_INSTRUCTION_TABLE_CONFIG = {
  tableId: 'org-ai-ins',
} as const;

export const AI_INSTRUCTION_FILTER_URL_KEYS = {
  [AI_INSTRUCTION_FILTERABLE_FIELDS.NAME]: 'in',
  [AI_INSTRUCTION_FILTERABLE_FIELDS.TYPE]: 'it',
  [AI_INSTRUCTION_FILTERABLE_FIELDS.FILE]: 'if',
  [AI_INSTRUCTION_FILTERABLE_FIELDS.SUMMARY]: 'is',
  [AI_INSTRUCTION_FILTERABLE_FIELDS.PROJECTS]: 'ip',
  [AI_INSTRUCTION_FILTERABLE_FIELDS.USERS]: 'iu',
} as const satisfies Record<AiInstructionFilterableField, string>;

export const AI_INSTRUCTION_TYPE_OPTIONS = [
  { value: AI_INSTRUCTION_TYPES.RULE, label: 'Rule' },
  { value: AI_INSTRUCTION_TYPES.SKILL, label: 'Skill' },
  { value: AI_INSTRUCTION_TYPES.COMMAND, label: 'Command' },
  { value: AI_INSTRUCTION_TYPES.CONFIGURATION, label: 'Configuration' },
];

/**
 * Badge color classes for AI instruction types.
 * Used by both TypeCell and TypeFilterOption components.
 */
export const TYPE_BADGE_CLASSES: Record<string, string> = {
  rule: 'bg-blue-100 text-blue-700 border-blue-200',
  skill: 'bg-green-100 text-green-700 border-green-200',
  command: 'bg-orange-100 text-orange-700 border-orange-200',
  configuration: 'bg-violet-100 text-violet-700 border-violet-200',
};

/**
 * KIR-3937 — quarantine label. A prevented finding on this path means Kirin
 * took a protective action; instruction/skill files are emptied, others may
 * only be backed up or surgically edited (KIR-3562). Without the badge the
 * row is indistinguishable from a file the user genuinely left empty. Amber
 * rather than destructive: Kirin protecting the user is not an error state.
 */
export const QUARANTINED_BADGE_LABEL = 'Quarantined';
export const QUARANTINED_BADGE_CLASS =
  'bg-amber-100 text-amber-800 border-amber-200';
// One string for both the name badge and the empty-summary hint: whether the
// row still shows pre-quarantine content depends on when the agent last
// reported it, so the copy must not promise either state. "Removed or emptied"
// covers surgical write-back and full-empty; it does not claim every
// prevented finding zeroed the file (FileTypeUnspecified is backup-only).
export const QUARANTINED_TOOLTIP =
  'Kirin quarantined this file — it was flagged as malicious and its contents were removed or emptied on disk. Anything shown here is from before the quarantine.';

// Explicit map dodges hyphenated capitalize traps (e.g. "Ide-setting").
// Unknown values fall back to plain capitalize via getCategoryBadgeLabel.
export const CATEGORY_BADGE_LABELS: Record<string, string> = {
  rule: 'Rule',
  skill: 'Skill',
  command: 'Command',
  configuration: 'Configuration',
};

export function getCategoryBadgeLabel(category: string): string {
  return (
    CATEGORY_BADGE_LABELS[category] ??
    category.charAt(0).toUpperCase() + category.slice(1)
  );
}
